/**
 * Smart truncation utilities for UI display
 */

interface TruncationOptions {
  maxLength?: number;
  maxDepth?: number;
  preserveStructure?: boolean;
}

/**
 * Truncate a string to a specific length with ellipsis
 */
function truncateString(str: string, maxLength: number = 500): string {
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
}

/**
 * Truncate JSON object values while preserving structure
 */
function truncateJsonValues(obj: any, maxLength: number = 500, currentDepth: number = 0, maxDepth: number = 10): any {
  if (currentDepth >= maxDepth) {
    return '[Max depth reached]';
  }

  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === 'string') {
    return truncateString(obj, maxLength);
  }

  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => truncateJsonValues(item, maxLength, currentDepth + 1, maxDepth));
  }

  if (typeof obj === 'object') {
    const truncated: any = {};
    for (const [key, value] of Object.entries(obj)) {
      truncated[key] = truncateJsonValues(value, maxLength, currentDepth + 1, maxDepth);
    }
    return truncated;
  }

  return obj;
}

/**
 * Smart truncation that handles JSON and regular text
 */
export function smartTruncate(content: string | any, options: TruncationOptions = {}): string {
  const { maxLength = 500, maxDepth = 10, preserveStructure = true } = options;

  // If content is already a string, check if it's JSON
  if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content);
      if (preserveStructure) {
        const truncated = truncateJsonValues(parsed, maxLength, 0, maxDepth);
        return JSON.stringify(truncated, null, 2);
      } else {
        return truncateString(content, maxLength * 10); // Allow more space for JSON
      }
    } catch {
      // Not JSON, just truncate as string
      return truncateString(content, maxLength * 2); // Allow more space for regular text
    }
  }

  // If content is an object, truncate its values
  if (typeof content === 'object' && content !== null) {
    if (preserveStructure) {
      const truncated = truncateJsonValues(content, maxLength, 0, maxDepth);
      return JSON.stringify(truncated, null, 2);
    } else {
      const jsonStr = JSON.stringify(content, null, 2);
      return truncateString(jsonStr, maxLength * 10);
    }
  }

  // Fallback to string conversion and truncation
  return truncateString(String(content), maxLength);
}

/**
 * Truncate tool response with special handling for different response types
 */
export function truncateToolResponse(response: any, functionName: string = ''): string {
  // Special handling for specific tools
  if (functionName === 'get_all_requests_with_ids' || functionName === 'search_requests') {
    // For HAR data, truncate individual request/response bodies
    if (Array.isArray(response)) {
      const truncated = response.map(item => {
        if (item && typeof item === 'object') {
          const truncatedItem = { ...item };
          if (truncatedItem.response_body) {
            truncatedItem.response_body = truncateString(truncatedItem.response_body, 200);
          }
          if (truncatedItem.request_body) {
            truncatedItem.request_body = truncateString(truncatedItem.request_body, 200);
          }
          if (truncatedItem.response_headers) {
            truncatedItem.response_headers = truncateString(JSON.stringify(truncatedItem.response_headers), 300);
          }
          return truncatedItem;
        }
        return item;
      });
      return JSON.stringify(truncated, null, 2);
    }
  }

  if (functionName === 'execute_code' || functionName === 'execute_parsing_code') {
    // For code execution, preserve structure but truncate output
    if (response && typeof response === 'object' && response.output) {
      const truncated = { ...response };
      truncated.output = truncateString(response.output, 1000);
      return JSON.stringify(truncated, null, 2);
    }
  }

  // Default smart truncation
  return smartTruncate(response, { maxLength: 500, preserveStructure: true });
}

/**
 * Check if content should be truncated and provide truncation info
 */
export function getTruncationInfo(originalContent: string, truncatedContent: string) {
  const originalLength = originalContent.length;
  const truncatedLength = truncatedContent.length;
  
  if (originalLength > truncatedLength) {
    const savedChars = originalLength - truncatedLength;
    return {
      isTruncated: true,
      originalLength,
      truncatedLength,
      savedChars,
      compressionRatio: Math.round((savedChars / originalLength) * 100)
    };
  }
  
  return { isTruncated: false, originalLength, truncatedLength: originalLength };
}